import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useApiRequest } from "../Login/Hooks/useApiRequest";

import NearbyVmList from "./NearbyVmList";
import RecentVmList from "./RecentVmList";
import NoVmFound from "./NoVmFound";
import { Box } from "@mui/material";
import Banner_Container from "../../Banners/container";
import SearchBar from "./SearchVm";
import LocationPage from "./LocationPage";
import Game from "../Game/component";
import TopNavbar from "./header";
import QrModal from "./Qr";
import BottomNarBar from "../../BottomNav/Bottom";
import Procced from "../../ProccedToPay/component";
import PendingPickup from "../PendingPickup/component";
import CategoryItem from "./Category/component";
import NoActiveMachine from "../VmInventory/NoActiveMachine";
import ReplaceCartModal from "../../PopUp/ReplaceCartPopUp";
import WaitingLoder from "../../Loader/component";
import LoyaltyCongratsDialog from "./LoyaltyCongratsDialog";
import NoInternet from "../../NoInterNet/component";
import ServerErrorPage from "../../ServerError/ServerErrorPage";

// ─── Constants ────────────────────────────────────────────────────────────────

const GEO_ERROR_MESSAGES = {
  1: "Location access was denied. Please enable it in your browser settings.",
  2: "Your location is currently unavailable. Please try again.",
  3: "Location request timed out. Please try again.",
};

// ─── Pure helpers ─────────────────────────────────────────────────────────────

/**
 * Returns auth headers read fresh from localStorage on every call,
 * so stale closures never send an expired token.
 */
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("UserJWTAuth")}`,
});

/**
 * Merges cart counts into the slot details of each VM so child components
 * always receive pre-computed `myCount` without needing cart access.
 */
const mergeCartCountsIntoSlots = (vmList = [], cartItems = [], cartMachineId) =>
  vmList.map((vm) => ({
    ...vm,
    slotDetails: vm.slotDetails.map((slot) => {
      const matched =
        String(cartMachineId) === String(vm.vmId)
          ? cartItems.find((item) => item.slotId === slot.slotId)
          : null;
      return { ...slot, myCount: matched ? matched.count : 0 };
    }),
  }));

/**
 * Derives bottom padding so the main scroll area never hides behind
 * floating bars (cart strip + pending-pickup strip + bottom nav).
 */
const getBottomPadding = (activeOrders = [], preCart = []) => {
  const hasCart = preCart.length > 0;
  const hasActive = activeOrders.length > 0;
  if (hasCart && hasActive) return "135px";
  if (hasCart) return "50px";
  if (hasActive && activeOrders.length > 1) return "115px";
  if (hasActive) return "80px";
  return "0px";
};

// ─── Component ────────────────────────────────────────────────────────────────

const VmlistContainer = () => {
  const { makeApiRequest } = useApiRequest();
  const navigate = useNavigate();
  const location = useLocation();

  // ── VM lists ──────────────────────────────────────────────────────────────
  const [nearbyVmList, setNearbyVmList] = useState([]);
  const [recentVmList, setRecentVmList] = useState([]);
  const [vmLoading, setVmLoading] = useState(false);

  // ── Cart ──────────────────────────────────────────────────────────────────
  const [preCart, setPreCart] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [cartMachineId, setCartMachineId] = useState(null);
  const [replaceSlot, setReplaceSlot] = useState(null);
  const [showReplaceModal, setShowReplaceModal] = useState(false);

  // ── Orders ────────────────────────────────────────────────────────────────
  const [activeOrders, setActiveOrders] = useState([]);

  // ── Categories ────────────────────────────────────────────────────────────
  const [categories, setCategories] = useState([]);
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [categoryVmItems, setCategoryVmItems] = useState([]);

  // ── User / loyalty ────────────────────────────────────────────────────────
  const [userDetails, setUserDetails] = useState(null);
  const [milestoneData, setMilestoneData] = useState({
    currentLevelMilestoneCount: 0,
    reward: null,
    levelId: 1,
  });
  const [showCongrats, setShowCongrats] = useState(false);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [loader, setLoader] = useState(false);
  const [showLocationPrompt, setShowLocationPrompt] = useState(true);
  const [showQrModal, setShowQrModal] = useState(false);
  const [unknownUuid, setUnknownUuid] = useState(null); // truthy → NoActiveMachine screen
  const [viewAll, setViewAll] = useState(false);
  const [noInternet, setNoInternet] = useState(false);
  const [serverError, setServerError] = useState(false);

  // Keep a stable ref to fetchPreCart so the online listener can call the
  // latest version without being re-registered on every render.
  const fetchPreCartRef = useRef(null);

  // Coords key (~110m precision) of the last outlet/search request, so the
  // cached-coords load and the GPS callback don't both hit the API.
  const lastVmFetchKeyRef = useRef(null);

  // ─── Error classification ─────────────────────────────────────────────────

  const isServerError = useCallback((error) => {
    const status = error?.response?.status;
    const code = error?.response?.data?.status?.code;
    return (
      code === "INTERNAL_SERVER_ERROR" || [500, 502, 503, 504].includes(status)
    );
  }, []);

  /**
   * Central error handler.
   * Returns true if it handled the error (caller should bail out early).
   */
  const handleError = useCallback(
    (error, fallbackMessage) => {
      if (isServerError(error)) {
        setServerError(true);
        setLoader(false);
        return true;
      }
      if (error?.code === "ERR_NETWORK") {
        setServerError(true);
        setLoader(false);
        return true;
      }
      if (fallbackMessage) {
        toast.error(fallbackMessage, { autoClose: 1500 });
      }
      return false;
    },
    [isServerError]
  );

  // ─── Network listeners ────────────────────────────────────────────────────

  useEffect(() => {
    if (!navigator.onLine) {
      setNoInternet(true);
      setLoader(false);
    }

    const goOffline = () => setNoInternet(true);

    const goOnline = () => {
      setNoInternet(false);
      setServerError(false);
      setLoader(false);
      // Use ref so we always call the latest version of fetchPreCart
      fetchPreCartRef.current?.();
      fetchActiveOrders();
      fetchCategories();
      requestGeolocation();
    };

    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Auth guard ───────────────────────────────────────────────────────────

  useEffect(() => {
    const token = localStorage.getItem("UserJWTAuth");
    if (!token) {
      navigate("/login", {
        state: { from: location.pathname + location.search },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Bootstrap ────────────────────────────────────────────────────────────

  useEffect(() => {
    // If coordinates are already cached, start loading VMs immediately.
    const lat = localStorage.getItem("lat");
    const lng = localStorage.getItem("lng");
    if (lat && lng && navigator.onLine) fetchVmList(lat, lng);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!navigator.onLine) return;

    const isGuest = !!localStorage.getItem("Guest_OauthToken");
    if (!isGuest) fetchUserDetails();

    fetchPreCart();
    fetchActiveOrders();
    fetchCategories();
    requestGeolocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Geolocation ─────────────────────────────────────────────────────────

  // force=true for explicit user taps (refresh location), so they always refetch.
  const requestGeolocation = useCallback((force = false) => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by this browser.", {
        autoClose: 2000,
      });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      ({ coords: { latitude, longitude } }) => {
        localStorage.setItem("lat", latitude);
        localStorage.setItem("lng", longitude);
        fetchVmList(latitude, longitude, force === true);
      },
      (err) => {
        const message =
          GEO_ERROR_MESSAGES[err.code] || "An unknown location error occurred.";
        toast.error(message, { autoClose: 2000 });
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── VM list ──────────────────────────────────────────────────────────────

  const fetchVmList = useCallback(
    (lat, lng, force = false) => {
      setShowLocationPrompt(false);

      const key = `${Number(lat).toFixed(3)},${Number(lng).toFixed(3)}`;
      if (!force && lastVmFetchKeyRef.current === key) return;
      lastVmFetchKeyRef.current = key;

      setVmLoading(true);

      makeApiRequest({
        url: `${process.env.REACT_APP_API_BASE_URL}/consumerbe/api/v2/outlet/search?nearbyLimit=50&recentLimit=6&lat=${lat}&lng=${lng}`,
        method: "GET",
        headers: getAuthHeaders(),
        handleSuccess: (res) => {
          setNearbyVmList(res?.data?.data?.nearbyMachines || []);
          setRecentVmList(res?.data?.data?.recentMachines || []);
          setVmLoading(false);
        },
        handleError: (error) => {
          setVmLoading(false);
          lastVmFetchKeyRef.current = null; // allow retry after a failure
          handleError(error);
        },
      });
    },
    [makeApiRequest, handleError]
  );

  const fetchMachineByUUID = useCallback(
    (uuid) => {
      makeApiRequest({
        url: `${process.env.REACT_APP_API_BASE_URL}/consumerbe/api/v2/outlet/machinebyuuid/${uuid}`,
        method: "GET",
        headers: getAuthHeaders(),
        handleSuccess: (res) => {
          const machineId = res?.data?.data?.machineId;
          if (machineId) {
            navigate(`/items?machineId=${machineId}&value=${uuid}`, {
              state: { from: location.pathname + location.search },
            });
          } else {
            toast.error("Machine not found for this QR code.", {
              position: "top-right",
              autoClose: 1500,
            });
          }
        },
        handleError: (error) => {
          if (handleError(error)) return;
          const code = error?.response?.data?.status?.code;
          setUnknownUuid(uuid);
          if (code === "UNAUTHORIZED") {
            navigate(`/login?value=${uuid}`, {
              state: { from: location.pathname + location.search },
            });
          }
        },
      });
    },
    [makeApiRequest, navigate, location, handleError]
  );

  // ─── User / loyalty ───────────────────────────────────────────────────────

  const fetchUserDetails = useCallback(() => {
    if (!localStorage.getItem("UserJWTAuth")) return;

    makeApiRequest({
      url: `${process.env.REACT_APP_API_BASE_URL}/consumerbe/api/v2/user/loyalty/details`,
      method: "GET",
      headers: getAuthHeaders(),
      handleSuccess: (res) => {
        const userData = res?.data?.data;
        setUserDetails(userData);

        const reward =
          userData?.levelRewards?.find(
            (r) => r.milestoneCount === userData.currentLevelMilestoneCount
          ) || null;

        const milestone = {
          currentLevelMilestoneCount: userData.currentLevelMilestoneCount,
          reward,
          levelId: userData.levelId,
          levelImage: userData.levelImage,
        };
        setMilestoneData(milestone);

        const uniqueKey = `levelId_${userData.levelId}_milestone_${userData.currentLevelMilestoneCount}`;
        if (localStorage.getItem("lastShownMilestone") !== uniqueKey) {
          setShowCongrats(true);
        }
      },
      handleError: (error) => handleError(error),
    });
  }, [makeApiRequest, handleError]);

  const handleCloseCongrats = useCallback(() => {
    const key = `levelId_${milestoneData.levelId}_milestone_${milestoneData.currentLevelMilestoneCount}`;
    localStorage.setItem("lastShownMilestone", key);
    setShowCongrats(false);
  }, [milestoneData]);

  // ─── Cart ─────────────────────────────────────────────────────────────────

  const fetchPreCart = useCallback(() => {
    const token = localStorage.getItem("UserJWTAuth");
    if (!token) return;

    makeApiRequest({
      url: `${process.env.REACT_APP_API_BASE_URL}/consumerbe/api/v2/cart`,
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
      handleSuccess: (res) => {
        const items = res?.data?.data?.items || [];
        const machineId = res?.headers?.["machineid"];

        setCartMachineId(machineId);
        localStorage.setItem("machineId", machineId);

        const total = items.reduce(
          (sum, item) => sum + item.amount * item.count,
          0
        );
        setTotalAmount(total);
        setPreCart(items);

        // Refresh category view with updated cart counts.
        if (activeCategoryId) {
          fetchCategoriesWise(activeCategoryId, items, machineId);
        }
      },
      handleError: (error) => {
        if (isServerError(error)) {
          setServerError(true);
          return;
        }
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          localStorage.removeItem("UserJWTAuth");
          return;
        }
        const code = error?.response?.data?.status?.code || "Error";
        toast.error(code, { position: "top-right", autoClose: 1000 });
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [makeApiRequest, activeCategoryId, isServerError]);

  // Keep the ref in sync so the online listener always calls the latest version.
  fetchPreCartRef.current = fetchPreCart;

  const handleCartOperation = useCallback(
    (e, _index, slotId, currentCount, operation, machineId) => {
      e.stopPropagation();
      setLoader(true);

      const token = localStorage.getItem("UserJWTAuth");
      const nextCount = operation === "add" ? currentCount + 1 : currentCount - 1;

      // Remember which slot the user tried to add so we can retry after
      // the replace-cart confirmation.
      if (preCart.length > 0) {
        setReplaceSlot({ slotId, machineId, currentCount: 0 });
      }

      makeApiRequest({
        url: `${process.env.REACT_APP_API_BASE_URL}/consumerbe/api/v2/cart?machineId=${machineId}&replace-cart=${showReplaceModal}`,
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
        data: [{ slotId: parseInt(slotId, 10), count: nextCount }],
        handleSuccess: (res) => {
          setLoader(false);
          localStorage.setItem("card_id", res.headers["cart-id"]);
          setPreCart(res?.data?.data?.items || []);
          fetchPreCart();
        },
        handleError: (error) => {
          setLoader(false);
          if (handleError(error)) return;
          const message = error?.response?.data?.status?.message || "";
          if (message === "Cart contains items from another machine") {
            setShowReplaceModal(true);
          }
          toast.error(message, { position: "top-right", autoClose: 1500 });
        },
      });
    },
    [makeApiRequest, preCart, showReplaceModal, handleError, fetchPreCart]
  );

  const confirmReplaceCart = useCallback(
    (e) => {
      setShowReplaceModal(false);
      if (replaceSlot) {
        handleCartOperation(
          e,
          null,
          replaceSlot.slotId,
          replaceSlot.currentCount,
          "add",
          replaceSlot.machineId
        );
      }
    },
    [replaceSlot, handleCartOperation]
  );

  // ─── Active orders ────────────────────────────────────────────────────────

  const fetchActiveOrders = useCallback(() => {
    makeApiRequest({
      url: `${process.env.REACT_APP_API_BASE_URL}/consumerbe/api/v2/order/active`,
      method: "GET",
      headers: getAuthHeaders(),
      handleSuccess: (res) => setActiveOrders(res?.data?.data || []),
      handleError: (error) => handleError(error),
    });
  }, [makeApiRequest, handleError]);

  // ─── Categories ───────────────────────────────────────────────────────────

  const fetchCategories = useCallback(() => {
    makeApiRequest({
      url: `${process.env.REACT_APP_API_BASE_URL}/consumerbe/api/v2/item/category`,
      method: "GET",
      headers: getAuthHeaders(),
      handleSuccess: (res) => {
        setCategories(res?.data?.data || []);
        setLoader(false);
      },
      handleError: (error) => {
        handleError(error);
        setLoader(false);
      },
    });
  }, [makeApiRequest, handleError]);

  const fetchCategoriesWise = useCallback(
    (id, cartItems, preMachineId) => {
      setActiveCategoryId(id);
      const lat = localStorage.getItem("lat");
      const lng = localStorage.getItem("lng");

      makeApiRequest({
        // given by sameer
       // url: `${process.env.REACT_APP_API_BASE_URL}/consumerbe/api/v2/outlet-wise/inventory?page=0&size=100&lat=${lat}&lng=${lng}&categoryId=${id}&isDigital=true`,
       // by vivek 
       url: `${process.env.REACT_APP_API_BASE_URL}/consumerbe/api/v2/outlet-wise/inventory?page=0&size=100&lat=${lat}&lng=${lng}&categoryId=${id}`,
        method: "GET",
        headers: getAuthHeaders(),
        handleSuccess: (res) => {
          const merged = mergeCartCountsIntoSlots(
            res?.data?.data || [],
            cartItems ?? preCart,
            preMachineId ?? cartMachineId
          );
          setCategoryVmItems(merged);
          setLoader(false);
        },
        handleError: (error) => {
          handleError(error);
          setLoader(false);
        },
      });
    },
    [makeApiRequest, preCart, cartMachineId, handleError]
  );

  // ─── Early returns (error / loading screens) ──────────────────────────────

  if (noInternet) return <NoInternet />;
  if (serverError) return <ServerErrorPage />;
  if (showLocationPrompt)
    return (
      <LocationPage
        fetchVmList={fetchVmList}
        setshowLoction={setShowLocationPrompt}
      />
    );
  if (unknownUuid)
    return (
      <NoActiveMachine
        uuid={unknownUuid}
        gotohome={() => setUnknownUuid(null)}
      />
    );

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <LoyaltyCongratsDialog
        open={showCongrats}
        onClose={handleCloseCongrats}
        medalImage={milestoneData?.levelImage}
        milestoneData={milestoneData}
      />

      {loader && <WaitingLoder />}

      {showQrModal ? (
        <QrModal
          ShowQRFun={() => setShowQrModal(true)}
          qrModelflag={showQrModal}
          CloseQrFun={() => setShowQrModal(false)}
          fetchMachineByUUID={fetchMachineByUUID}
        />
      ) : (
        <>
          {showReplaceModal && (
            <ReplaceCartModal
              replaceMyCardFun={confirmReplaceCart}
              setReplaceCard={setShowReplaceModal}
            />
          )}

          <TopNavbar
            LoctionPinHeader
            QrMarkHeader
            shareLocation={() => requestGeolocation(true)}
            ShowQRFun={() => setShowQrModal(true)}
            preCart={preCart}
          />

          <Box
            sx={{
              padding: 2,
              position: "relative",
              minHeight: "100vh",
              background:
                "radial-gradient(circle, rgba(245,233,233,0.4) 0%, rgba(185,245,247,0.34) 100%)",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                background: "rgba(0,0,0,0.1)",
                zIndex: 0,
                animation: "fadeOverlay 5s ease-in-out infinite alternate",
              },
              "@keyframes fadeOverlay": {
                from: { opacity: 0.2 },
                to: { opacity: 0.4 },
              },
            }}
          >
            <Box sx={{ position: "relative", zIndex: 1 }}>
              {userDetails && <Game data={userDetails} />}

              <Banner_Container vmid={0} />
              <SearchBar />

              <CategoryItem
                categories={categories}
                fetchCategoriesWise={fetchCategoriesWise}
                categoriesWiseVmItems={categoryVmItems}
                setCategoriesWiseVmItems={setCategoryVmItems}
                handleCartOpertation={handleCartOperation}
                setViewall={setViewAll}
                viewall={viewAll}
              />

              <div
                style={{
                  paddingBottom: getBottomPadding(activeOrders, preCart),
                }}
              >
                {recentVmList.length > 0 && (
                  <RecentVmList
                    recentVmlist={recentVmList}
                    loading={vmLoading}
                    onFetchLocation={requestGeolocation}
                  />
                )}
                {nearbyVmList.length > 0 && (
                  <NearbyVmList
                    nearbyVmlist={nearbyVmList}
                    loading={vmLoading}
                    onFetchLocation={requestGeolocation}
                  />
                )}
              </div>

              {nearbyVmList.length === 0 && recentVmList.length === 0 && (
                <NoVmFound />
              )}
            </Box>
          </Box>

          {preCart.length > 0 && (
            <Procced
              preCart={preCart}
              totalAmount={totalAmount}
              bottom="90px"
              page="vmlist"
            />
          )}
          {activeOrders.length > 0 && (
            <PendingPickup
              activeOrders={activeOrders}
              preCart={preCart}
              page="vmlist"
            />
          )}

          <BottomNarBar />
        </>
      )}
    </>
  );
};

export default VmlistContainer;